<template>
  <div class="my-ads-detail-page">
    <div
      v-if="isLoadingAd"
      class="detail-loading"
    >
      <b-spinner label="Loading ad..." />
    </div>
    <b-row v-else>
      <b-col
        lg="4"
        class="mb-2 mb-lg-0"
      >
        <b-card class="ad-left-card">
          <div class="ad-image-wrap">
            <img
              v-if="adPreviewImage"
              :src="adPreviewImage"
              alt="Ad Preview"
              class="ad-image"
            >
            <div
              v-else
              class="ad-image-placeholder"
            >
              No image
            </div>
          </div>

          <p class="ad-detail-label">
            Ad Details
          </p>

          <div class="ad-details-text">
            <p><strong>Title:</strong> {{ adDetails.title || '-' }}</p>
            <p><strong>Description:</strong> {{ adDetails.description || '-' }}</p>
            <p><strong>CTA:</strong> {{ adDetails.cta_text || '-' }}</p>
            <p v-if="adDetails.cta_link"><strong>Destination:</strong> {{ adDetails.cta_link }}</p>
            <p><strong>Create Date:</strong> {{ createdAt }}</p>
            <p><strong>Publish Date :</strong> {{ publishAt }}</p>
            <p><strong>Expire Date :</strong> {{ expireAt }}</p>
            <p><strong>Primary Category :</strong> {{ categoryName }}</p>
            <p><strong>Secondary Category :</strong> {{ secondaryCategoryName }}</p>
            <p><strong>Age Restriction :</strong> {{ ageRestrictionLabel }}</p>
            <p><strong>Status:</strong> {{ adDetails.status || '-' }}</p>
          </div>

          <div class="ad-footer-actions">
            <b-button
              class="extend-btn"
              :disabled="actionLoading"
              @click="extendAd"
            >
              <b-spinner v-if="actionLoading" small class="mr-50" />
              Extend
            </b-button>
            <b-button
              variant="light"
              class="stop-btn"
              :disabled="actionLoading"
              @click="stopAd"
            >
              Stop Ad
            </b-button>
          </div>
        </b-card>
      </b-col>

      <b-col lg="8">
        <div class="stats-grid">
          <b-card class="stat-card stat-card-plain">
            <h5>Ad Performance</h5>
            <div class="metric-row">
              <span class="metric-big">{{ dailyAvgLikes }}</span>
              <img
                src="/svg/ads/Performance.svg"
                alt="Ad performance graph"
                class="mini-graph"
              >
            </div>
            <small>Daily average likes</small>
          </b-card>

          <b-card class="stat-card stat-card-plain">
            <h5>Likes</h5>
            <div class="metric-row">
              <span class="metric-big">{{ likesCount }}</span>
              <img
                src="/svg/ads/Likes.svg"
                alt="Likes graph"
                class="wave-graph"
              >
            </div>
          </b-card>

          <b-card class="stat-card stat-card-highlight">
            <h5>Clicks</h5>
            <div class="metric-big white">
              {{ clicksCount }}
            </div>
            <img
              src="/svg/ads/Click.svg"
              alt="Clicks"
              class="click-icon"
            >
          </b-card>
        </div>

        <div class="reaction-strip">
          <div
            v-for="reaction in reactions"
            :key="reaction.key"
            class="reaction-pill"
          >
            <div>
              <strong>{{ reactionCount(reaction.key) }}</strong>
              <small>{{ reaction.label }}</small>
            </div>
            <img
              :src="reaction.iconSrc"
              :alt="reaction.label"
              class="reaction-icon"
            >
          </div>
        </div>

        <b-card
          no-body
          class="activity-card"
        >
          <b-table
            responsive
            class="activity-table mb-0"
            :items="paginatedActivityRows"
            :fields="activityFields"
            show-empty
            empty-text="No activity data available"
          >
            <template #cell(user)="{ item }">
              <div class="user-cell">
                <b-avatar
                  size="28"
                  :src="item.userAvatar || null"
                  :text="item.userInitials"
                  class="user-avatar"
                />
                <span>{{ item.userName }}</span>
              </div>
            </template>
            <template #cell(activity)="{ item }">
              <span class="activity-type-label">{{ item.activity }}</span>
            </template>
          </b-table>

          <div
            v-if="activityTotalRows"
            class="activity-footer"
          >
            <small>Showing {{ activityPaginationStart }} to {{ activityPaginationEnd }} of {{ activityTotalRows }} entries</small>
            <b-pagination
              v-if="showActivityPagination"
              v-model="page"
              :total-rows="activityTotalRows"
              :per-page="activityPerPage"
              size="sm"
              first-number
              last-number
              class="mb-0"
            >
              <template #prev-text>
                Previous
              </template>
              <template #next-text>
                Next
              </template>
            </b-pagination>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BTable,
  BPagination,
  BAvatar,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import { confirmAction, notifySuccess, notifyError } from '@/utils/uiActions'
import { formatDisplayDateTime } from '@/utils/adCreate'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { getInitials } from '@/utils/vendorNotifications'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BTable,
    BPagination,
    BAvatar,
  },
  data() {
    return {
      page: 1,
      activityPerPage: 8,
      reactions: [
        { key: 'like', label: 'Like', iconSrc: '/svg/ads/like.svg' },
        { key: 'dislike', label: 'Dislike', iconSrc: '/svg/ads/dislike.svg' },
        { key: 'saved', label: 'Saved', iconSrc: '/svg/ads/saved.svg' },
        { key: 'report', label: 'Report', iconSrc: '/svg/ads/report.svg' },
        { key: 'share', label: 'Share', iconSrc: '/svg/ads/share.svg' },
      ],
      activityFields: [
        { key: 'date', label: 'DATE' },
        { key: 'user', label: 'USER' },
        { key: 'activity', label: 'ACTIVITY TYPE' },
      ],
      activityRows: [
      ],
      adDetails: {},
      isLoadingAd: false,
      actionLoading: false,
    }
  },
  mounted() {
    this.fetchAdDetails()
  },
  computed: {
    adPreviewImage() {
      return this.resolveMediaUrl(this.adDetails.image)
    },
    createdAt() {
      return formatDisplayDateTime(this.adDetails.dates?.created_at)
    },
    publishAt() {
      return formatDisplayDateTime(this.adDetails.dates?.publish_at)
    },
    expireAt() {
      return formatDisplayDateTime(
        this.adDetails.expires_at || this.adDetails.dates?.expire_at,
      )
    },
    categoryName() {
      return (this.adDetails.category && this.adDetails.category.name) || '-'
    },
    secondaryCategoryName() {
      return (this.adDetails.secondary_category && this.adDetails.secondary_category.name) || '-'
    },
    ageRestrictionLabel() {
      const minAge = Number(this.adDetails.min_age || 0)
      return minAge > 0 ? `${minAge} years and older` : 'No age restriction'
    },
    dailyAvgLikes() {
      return (this.adDetails.performance && this.adDetails.performance.daily_avg_likes) || '0'
    },
    likesCount() {
      return (this.adDetails.stats && this.adDetails.stats.likes) || '0'
    },
    clicksCount() {
      return (this.adDetails.stats && this.adDetails.stats.clicks) || '0'
    },
    activityTotalRows() {
      return this.activityRows.length
    },
    paginatedActivityRows() {
      const start = (this.page - 1) * this.activityPerPage
      return this.activityRows.slice(start, start + this.activityPerPage)
    },
    activityPaginationStart() {
      if (!this.activityTotalRows) return 0
      return (this.page - 1) * this.activityPerPage + 1
    },
    activityPaginationEnd() {
      if (!this.activityTotalRows) return 0
      return Math.min(this.page * this.activityPerPage, this.activityTotalRows)
    },
    showActivityPagination() {
      return this.activityTotalRows > this.activityPerPage
    },
  },
  watch: {
    '$route.params.id': function routeIdWatcher() {
      this.page = 1
      this.fetchAdDetails()
    },
    activityTotalRows() {
      const maxPage = Math.max(1, Math.ceil(this.activityTotalRows / this.activityPerPage))
      if (this.page > maxPage) this.page = maxPage
    },
  },
  methods: {
    resolveMediaUrl,
    normalizeActivityType(value) {
      const raw = String(value || '').trim()
      if (!raw) return '-'
      return raw.charAt(0).toUpperCase() + raw.slice(1)
    },
    mapActivityRows(data = {}) {
      const list = Array.isArray(data.activity_data)
        ? data.activity_data
        : (Array.isArray(data.activities?.list) ? data.activities.list : [])

      return list.map(item => {
        const user = item.user || {}
        const userName = user.name || item.user_name || '-'
        return {
          date: formatDisplayDateTime(item.date),
          activity: this.normalizeActivityType(item.activityType || item.activity_type),
          userName,
          userAvatar: resolveMediaUrl(user.profileimage || user.profile_image || user.avatar || ''),
          userInitials: getInitials(userName),
        }
      })
    },
    reactionCount(key) {
      const stats = this.adDetails.stats || {}
      if (stats[key] !== undefined && stats[key] !== null) return stats[key]
      const pluralKey = `${key}s`
      if (stats[pluralKey] !== undefined && stats[pluralKey] !== null) return stats[pluralKey]
      return 0
    },
    async fetchAdDetails() {
      this.isLoadingAd = true
      try {
        const response = await vendorPanelApi.adsDetailById({ id: this.$route.params.id })
        const data = response?.data?.data || {}
        this.adDetails = data
        this.page = 1
        this.activityRows = this.mapActivityRows(data)
      } catch (error) {
        this.adDetails = {}
        this.activityRows = []
        this.page = 1
      } finally {
        this.isLoadingAd = false
      }
    },
    extendAd() {
      confirmAction(
        this,
        { title: 'Extend ad?', text: 'Extend the run time for this ad by 30 days?', confirmButtonText: 'Extend' },
        async () => {
          if (this.actionLoading) return
          this.actionLoading = true
          try {
            await vendorPanelApi.extendAd({ ad_id: this.$route.params.id, days: 30 })
            await this.fetchAdDetails()
            notifySuccess(this, 'Ad extended successfully.')
          } catch (error) {
            notifyError(this, 'Unable to extend ad right now.')
          } finally {
            this.actionLoading = false
          }
        },
      )
    },
    stopAd() {
      confirmAction(
        this,
        { title: 'Stop ad?', text: 'This ad will stop running for users.', confirmButtonText: 'Stop' },
        async () => {
          if (this.actionLoading) return
          this.actionLoading = true
          try {
            await vendorPanelApi.stopAd({ ad_id: this.$route.params.id })
            await this.fetchAdDetails()
            notifySuccess(this, 'Ad stopped successfully.')
          } catch (error) {
            notifyError(this, 'Unable to stop ad right now.')
          } finally {
            this.actionLoading = false
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.my-ads-detail-page {
  .detail-loading {
    min-height: 320px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ad-left-card {
    border: 1px solid #ebeaf1;
    border-radius: 8px;
    box-shadow: 0 4px 14px rgba(60, 63, 89, 0.1);
  }

  .ad-image-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 0.85rem;
  }

  .ad-image {
    width: 170px;
    height: 220px;
    border-radius: 14px;
    object-fit: cover;
  }

  .ad-image-placeholder {
    width: 170px;
    height: 220px;
    border-radius: 14px;
    background: #f3f2f7;
    color: #9f9bb2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .ad-detail-label {
    color: #9f9bb2;
    margin-bottom: 0.8rem;
  }

  .ad-details-text {
    font-size: 0.95rem;
    color: #5e5873;

    p {
      margin-bottom: 0.5rem;
    }
  }

  .ad-footer-actions {
    display: flex;
    gap: 0.7rem;
    margin-top: 1rem;
  }

  .extend-btn {
    min-width: 84px;
    background: #011425 !important;
    border-color: #011425 !important;
    color: #fff !important;

    &:hover,
    &:focus,
    &:active {
      background: #011425 !important;
      border-color: #011425 !important;
      color: #fff !important;
    }
  }

  .stop-btn {
    min-width: 84px;
    color: #d96b6c;
    background: #ffe9e9;
    border-color: #ffe9e9;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem;
  }

  .stat-card {
    border: 1px solid #ebeaf1;
    border-radius: 10px;
    box-shadow: none;

    h5 {
      color: #4b465c;
      font-size: 1rem;
      margin-bottom: 0.4rem;
    }
  }

  .stat-card-plain small {
    color: #9f9bb2;
  }

  .metric-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.6rem;
  }

  .metric-big {
    font-size: 2rem;
    line-height: 1;
    font-weight: 700;
    color: #2f2b3d;
  }

  .metric-big.white {
    color: #fff;
  }

  .mini-graph {
    width: 88px;
    height: 48px;
    object-fit: contain;
    display: block;
  }

  .wave-graph {
    width: 110px;
    height: 45px;
    object-fit: contain;
    display: block;
  }

  .stat-card-highlight {
    background: linear-gradient(145deg, #6670f3, #5e66df);
    border: none;
    color: #fff;
    position: relative;

    h5 {
      color: #fff;
    }

    .click-icon {
      position: absolute;
      right: 1rem;
      bottom: 1rem;
      width: 30px;
      height: 30px;
      object-fit: contain;
      opacity: 0.9;
      display: block;
    }
  }

  .reaction-strip {
    margin-top: 0.85rem;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.6rem;
  }

  .reaction-pill {
    background: #8fd9f0;
    color: #fff;
    border-radius: 8px;
    padding: 0.52rem 0.65rem;
    display: flex;
    align-items: center;
    justify-content: space-between;

    strong,
    small {
      display: block;
      line-height: 1.1;
    }

    small {
      opacity: 0.95;
      font-size: 0.7rem;
    }

    .reaction-icon {
      width: 15px;
      height: 15px;
      object-fit: contain;
      flex-shrink: 0;
      display: block;
    }
  }

  .activity-card {
    margin-top: 0.95rem;
    border: 1px solid #ebeaf1;
    border-radius: 8px;
    box-shadow: none;
  }

  .activity-table {
    ::v-deep thead th {
      color: #7a758f;
      font-size: 0.76rem;
      font-weight: 700;
      border-top: 0;
      padding-top: 0.85rem;
      padding-bottom: 0.85rem;
    }

    ::v-deep tbody td {
      color: #5e5873;
      font-size: 0.92rem;
      vertical-align: middle;
      padding-top: 0.8rem;
      padding-bottom: 0.8rem;
    }
  }

  .user-cell {
    display: flex;
    align-items: center;
    gap: 0.55rem;
  }

  .user-avatar {
    background: #0f1729;
    color: #8dd9f0;
    font-weight: 700;
    font-size: 0.7rem;
  }

  .activity-footer {
    padding: 0.85rem 0.95rem 0.95rem;
    border-top: 1px solid #ebeaf1;
  }
}
</style>
